<template>
  <div class="themes-list" :class="{'themes-list--mobile': isMobile}">
    <ai-clear-button
      v-for="theme in themesListComputed"
      :key="theme.id"
      class="themes-list__item"
      :class="{ 'themes-list__item--selected': activeThemeId === theme.id }"
      @click="selectTheme(theme)"
    >
      <img :src="theme.url" :alt="theme.name" />
      <div class="themes-list__item__name">
        {{ theme.name }}
      </div>
    </ai-clear-button>
    <ai-clear-button
      v-if="themesList.length > themesListComputed.length"
      class="themes-list__item themes-list__item--more"
      @click="showThemesModal = true"
    >
      <img :src="randomTheme.url" :alt="randomTheme.name" />
      <div class="themes-list__item__text">
        {{ $t('editor.settings.more') }}
        {{ themesList.length - themesListComputed.length }}
      </div>
    </ai-clear-button>
  </div>
  <ai-bottom-modal
    :show-modal="showThemesModal"
    :handle-toggle="toggleThemesModalHandler"
    :title="$t('editor.settings.theme')"
    v-if="isMobile"
  >
    <div class="themes-list themes-list--mobile">
      <ai-clear-button
        v-for="theme in themesList"
        :key="theme.id"
        class="themes-list__item"
        :class="{ 'themes-list__item--selected': activeThemeId === theme.id }"
        @click="selectTheme(theme)"
      >
        <img :src="theme.url" :alt="theme.name" />
        <div class="themes-list__item__name">
          {{ theme.name }}
        </div>
      </ai-clear-button>
    </div>
  </ai-bottom-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEditorStore } from '@/store/editor';
import { ITheme } from '@/store/editor/types';
import { useCommonStore } from '@/store/common';
import { cloneDeep } from 'lodash';

import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
import AiBottomModal from '@/components/UI/AiBottomModal/AiBottomModal.vue';

const { generateImageData, themesList } = storeToRefs(useEditorStore());
const { isMobile } = storeToRefs(useCommonStore());

const activeThemeId = ref<string | null>(null);
const showThemesModal = ref<boolean>(false);

const themesLength = computed(() => (isMobile.value ? 8 : 100));

const themesListComputed = computed<ITheme[]>(() => {
  let result = cloneDeep(themesList.value);
  if (result.length > themesLength.value) {
    result = result.slice(0, themesLength.value);
    if (generateImageData.value.theme !== null) {
      const index = result.findIndex((themeItem: ITheme) => themeItem.id === generateImageData.value.theme);
      if (index === -1) {
        result.splice(0, 1, generateImageData.value.theme);
      }
    }
  }
  return result;
});

const randomTheme = computed(() => themesList.value[Math.floor(Math.random() * themesList.value.length)]);

const selectTheme = (theme: ITheme): void => {
  activeThemeId.value = theme.id;
  generateImageData.value.theme = theme;
};

const toggleThemesModalHandler = (): void => {
  showThemesModal.value = !showThemesModal.value;
};

onMounted(() => {
  if (themesList.value.length) {
    let defaultTheme = themesList.value[0];
    if (generateImageData.value.theme) {
      defaultTheme = generateImageData.value.theme;
    }
    activeThemeId.value = defaultTheme.id;
    generateImageData.value.theme = defaultTheme;
  }
  if (generateImageData.value.theme) {
    activeThemeId.value = generateImageData.value.theme.id;
  }
});

watch(themesList, () => {
  if (themesList.value.length) {
    let defaultTheme = themesList.value[0];
    if (generateImageData.value.theme) {
      defaultTheme = generateImageData.value.theme;
    }
    activeThemeId.value = defaultTheme.id;
    generateImageData.value.theme = defaultTheme;
  }
});
</script>

<style scoped lang="scss" src="./ThemesSettings.scss" />
